import React, { useMemo } from "react";
import { useData } from "../context/data/DataState";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Info } from "lucide-react";

const ActivitiesLinear = () => {
  const {
    communityId,
    seasonId,
    challengeId,
    data,
    activityId,
  } = useData();

  const navigate = useNavigate();

  // Flatten all activities into a single array for easier mapping
  const allActivities = useMemo(() => {
    if (!data || !data.community || !data.community[communityId]) return [];
    const challenge = data.community[communityId]?.season[seasonId]?.challenge[challengeId];
    if (!challenge) return [];

    const events = (challenge.event || []).map(e => ({ ...e, type: 'event', id: e.event_id, name: e.event_name, desc: e.event_description }));
    const tasks = (challenge.task || []).map(t => ({ ...t, type: 'task', id: t.task_id, name: t.task_name, desc: t.task_description }));
    const groups = (challenge.group || []).map(g => ({ ...g, type: 'group', id: g.group_id, name: g.group_name, desc: "Group Activity" }));

    // Filter out items that are part of a group from the main list if needed, 
    // but for the linear view we might want to show everything or just top level?
    // The original code filtered with `notingroup`. Let's respect that if possible, 
    // but `notingroup` helper wasn't imported in the original file correctly (it was imported but logic was inside map).
    // Let's assume we want to show all top-level items.

    // Actually, looking at previous code, it filtered `notingroup`.
    // Let's assume we want to list all valid navigable activities.

    return [...events, ...tasks, ...groups];
  }, [data, communityId, seasonId, challengeId]);

  if (allActivities.length === 0) return null;

  return (
    <div className="p-4">
      <Tabs
        value={activityId}
        onValueChange={(value) => navigate(`/edit/${value}`)}
        className="w-full"
      >
        <TabsList className="w-full overflow-x-auto flex-nowrap justify-start h-auto p-1 bg-transparent gap-2 no-scrollbar">
          {allActivities.map((activity, index) => (
            <TabsTrigger
              key={activity.id}
              value={activity.id}
              className="whitespace-nowrap px-4 py-2 rounded-full border border-gray-200 bg-white data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:border-primary transition-all shadow-sm"
            >
              Activity {index + 1}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="mt-4">
          {allActivities.map((activity) => (
            <TabsContent key={activity.id} value={activity.id} className="mt-0">
              <Card className="bg-blue-50/50 border-blue-100 shadow-none">
                <CardContent className="p-4 flex gap-3">
                  <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <h4 className="font-semibold text-blue-900 text-sm">
                      {activity.name}
                    </h4>
                    <p className="text-sm text-blue-700 leading-relaxed">
                      {activity.desc || "No description available."}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </div>
      </Tabs>
    </div>
  );
};

export default ActivitiesLinear;
